import { css } from '@emotion/css';
import React from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import houseIcon from 'src/assets/images/resort/ic-house.svg';
import autumnIcon from 'src/assets/images/resort/ic-season-autumn.svg';
import springIcon from 'src/assets/images/resort/ic-season-spring.svg';
import summerIcon from 'src/assets/images/resort/ic-season-summer.svg';
import winterIcon from 'src/assets/images/resort/ic-season-winter.svg';
import { FadeInImage } from 'src/components/shared/FadeInImage';

interface StructureHouseInfoProps {
  gardenId: string;
  gardenImage: string;
  roomNum: number;
  season: number;
}

export const StructureHouseInfo = ({
  gardenId,
  gardenImage,
  roomNum,
  season
}: StructureHouseInfoProps) => {
  const { t } = useTranslation(['resort', 'translation']);

  const floorString = getFloorString(roomNum, t);
  const seasonData = getSeason(season, t);

  return (
    <div
      className={css`
        display: flex;
        align-items: center;
        color: #756855;
        font-size: 14px;
        margin: 0 auto;
        .lot-image-container {
          position: relative;
          flex: 0 0 auto;
          margin-right: 12px;
        }
        .lot-image {
          border: 6px solid white;
          background-color: white;
          box-shadow: 0 0 12px rgba(0, 0, 0, 0.1);
          img {
            width: 90px;
            height: 83px;
          }
        }
        .lot-id-tag {
          position: absolute;
          top: -20px;
          left: 0;
          height: 20px;
          font-size: 12px;
          color: white;
          line-height: 1;
          letter-spacing: 0.1ch;
          min-width: 48px;
          text-align: center;
          background: #7da78f;
          padding: 4px 8px 3px 8px;
          border-radius: 16px 16px 0 0;
        }
        .lot-info {
          flex: 1 1 auto;
        }
        .info-row {
          display: flex;
          align-items: center;
          line-height: 1;
          background: #dfc973;
          color: #93844e;
          font-size: 16px;
          width: fit-content;
          padding: 4px 8px 4px 2px;
          border-left: 4px solid #cdb761;
          &::before {
            display: inline-block;
            width: 24px;
            height: 24px;
            margin-right: 2px;
          }
        }
        .info-row:nth-child(2) {
          margin: 8px 0 0;
        }
        .structure {
          &::before {
            content: '';
            background-image: url(${houseIcon});
            background-repeat: no-repeat;
            background-position: center;
            background-size: cover;
          }
        }
        .season {
          &::before {
            content: '';
            background-image: url(${seasonData.icon});
            background-repeat: no-repeat;
            background-position: center;
            background-size: cover;
          }
        }
      `}
    >
      <div role="img" className="lot-image-container" aria-labelledby="lot-image lot-id-tag ">
        <span id="lot-id-tag" className="lot-id-tag">
          {gardenId}
        </span>
        <div id="lot-image" className="lot-image">
          <FadeInImage src={gardenImage} width={252} height={232} ariaLabel={t('aria_map_image')} />
        </div>
      </div>
      <div className="lot-info">
        <p className="info-row structure">{floorString}</p>
        <p className="info-row season">{seasonData.name}</p>
      </div>
    </div>
  );
};

function getFloorString(roomNum: number, t: TFunction<('resort' | 'translation')[]>): string {
  switch (roomNum) {
    case 1:
      return t('one_story_house');
    case 2:
      return t('two_story_house');
    default:
      return '';
  }
}

function getSeason(seasonId: number, t: TFunction<('resort' | 'translation')[]>) {
  switch (seasonId) {
    case 0:
      return { icon: springIcon, name: t('translation:season_spring') };
    case 1:
      return { icon: summerIcon, name: t('translation:season_summer') };
    case 2:
      return { icon: autumnIcon, name: t('translation:season_autumn') };
    case 3:
      return { icon: winterIcon, name: t('translation:season_winter') };
    default:
      throw new Error('invalid season id');
  }
}
